import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowLeft } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ErrorState';
import { api } from '@/lib/api';
import { classifyApiError } from '@/lib/apiError';
import { formatSeasonName } from '@/lib/season/format';
import { useScopeChanged } from '@/lib/scopeEvent';

interface RotationItem {
  crop_id: string | null;
  crop_name: string | null;
  local_name: string | null;
  display_name: string | null;
  fields_count: number;
  planned_area_ha: number;
  actual_area_ha: number;
  /** Пар: занимает поле, но не сеется — в метрики сева не входит. */
  is_fallow?: boolean;
}

interface RotationSummary {
  season_code: string | null;
  season_year: number | null;
  total_planned_ha: number;
  total_actual_ha: number;
  total_fields: number;
  total_unplanned_fields?: number;
  total_unplanned_area_ha?: number;
  /**
   * Часть итогов, приходящаяся на пар (см. dashboard.py). AK-696: считается
   * по паровым ПОСЕВАМ (культура-первая классификация), а не по строкам —
   * пшеница с бэкфилл-`field_state_id` больше не утаскивает себя в пар.
   */
  total_fallow_planned_ha?: number;
  total_fallow_actual_ha?: number;
  /**
   * Пашня без единого занятия сезона — ни посева, ни пара (AK-696). Это НЕ
   * пар и НЕ «осталось засеять»: у этой земли нет ни плана, ни решения
   * парить — её просто никто не занял.
   */
  total_unoccupied_fields?: number;
  total_unoccupied_area_ha?: number;
  items: RotationItem[];
}

interface CropByFarmItem {
  farm_id: string;
  farm_name: string | null;
  planned_area_ha: number;
  actual_area_ha: number;
  fields_count: number;
}
interface CropByFarmOut {
  season_code: string | null;
  display_name: string | null;
  items: CropByFarmItem[];
}

// Earth Tech tokens — chart-1 (green-500) / chart-2 (amber-500).
// Recharts требует CSS-color values, не Tailwind classes.
const COLORS = {
  sown: 'hsl(142 71% 45%)', // primary-500
  unsown: 'hsl(38 92% 50%)', // accent-500
};

interface ChartRow {
  crop: string;
  sown: number;
  unsown: number;
}

export function CropsBarCard() {
  const [data, setData] = useState<RotationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  // Ошибка сервера/сети должна ВЫГЛЯДЕТЬ как ошибка, а не как пустой график
  // (AK-790 re-land #670, UAT 05.08 баг 12). Отдельно от `data`, чтобы
  // упавший refetch не сбрасывал уже нарисованный график.
  const [error, setError] = useState<string | null>(null);
  const [drillCrop, setDrillCrop] = useState<string | null>(null);
  const [drillData, setDrillData] = useState<CropByFarmOut | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  // Drill-down по хозяйствам — та же ошибка на 60 строк ниже (вердикт
  // head-web P1-3): catch шёл только в Sentry, после клика по культуре
  // человек видел пустоту.
  const [drillError, setDrillError] = useState<string | null>(null);

  const loadRotation = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<RotationSummary>('/api/v1/dashboard/rotation-summary')
      .then(setData)
      .catch((err) => {
        setError(classifyApiError(err, 'CropsBarCard.loadRotation').message);
      })
      .finally(() => setLoading(false));
  }, []);

  const loadDrill = useCallback((crop: string) => {
    setDrillLoading(true);
    setDrillError(null);
    const url = `/api/v1/dashboard/crop-by-farm?display_name=${encodeURIComponent(crop)}`;
    api
      .get<CropByFarmOut>(url)
      .then(setDrillData)
      .catch((err) => {
        setDrillError(classifyApiError(err, 'CropsBarCard.drillByFarm').message);
      })
      .finally(() => setDrillLoading(false));
  }, []);

  useEffect(() => {
    loadRotation();
  }, [loadRotation]);

  useScopeChanged(() => {
    setDrillCrop(null);
    loadRotation();
  });

  useEffect(() => {
    if (!drillCrop) {
      setDrillData(null);
      setDrillError(null);
      return;
    }
    loadDrill(drillCrop);
  }, [drillCrop, loadDrill]);

  const chartData = useMemo<ChartRow[]>(() => {
    if (!data || !Array.isArray(data.items)) return [];
    // Пар в столбцы «Засеяно / Осталось засеять» не попадает: он не сеется,
    // и его площадь в «осталось» читалась как «пар не засеян» (founder 21.08).
    // Он показан отдельной плиткой «Под паром».
    const agg = data.items.filter((it) => !it.is_fallow).map((it) => {
      const name = (
        it.display_name ||
        it.local_name ||
        it.crop_name ||
        '—'
      ).trim();
      const plan = Math.max(0, it.planned_area_ha ?? 0);
      const fact = Math.max(0, it.actual_area_ha ?? 0);
      const sown = Math.min(fact, plan || fact);
      const unsown = Math.max(0, plan - fact);
      return { name, sown, unsown, total: sown + unsown };
    });
    const grand = agg.reduce((s, x) => s + x.total, 0);
    const threshold = grand * 0.01;
    const big = agg
      .filter((x) => x.total >= threshold)
      .sort((a, b) => b.total - a.total);
    const small = agg.filter((x) => x.total < threshold && x.total > 0);
    const rows: ChartRow[] = big.map((x) => ({
      crop: x.name,
      sown: Math.round(x.sown),
      unsown: Math.round(x.unsown),
    }));
    if (small.length > 0) {
      const sown = small.reduce((s, x) => s + x.sown, 0);
      const unsown = small.reduce((s, x) => s + x.unsown, 0);
      rows.push({
        crop: `Другие (${small.length})`,
        sown: Math.round(sown),
        unsown: Math.round(unsown),
      });
    }
    return rows;
  }, [data]);

  const drillChartData = useMemo<ChartRow[]>(() => {
    if (!drillData) return [];
    const rows: ChartRow[] = [];
    for (const it of drillData.items) {
      const name = it.farm_name || '—';
      const plan = Math.max(0, it.planned_area_ha ?? 0);
      const fact = Math.max(0, it.actual_area_ha ?? 0);
      const sown = Math.min(fact, plan || fact);
      const unsown = Math.max(0, plan - fact);
      rows.push({
        crop: name,
        sown: Math.round(sown),
        unsown: Math.round(unsown),
      });
    }
    rows.sort((a, b) => b.sown + b.unsown - (a.sown + a.unsown));
    return rows;
  }, [drillData]);

  // Метрики сева считаются ТОЛЬКО по тому, что сеется: пар вычитается из
  // плана и из факта. Иначе процент сева занижен, а в «осталось засеять»
  // висит площадь, которую никто и не собирался засевать.
  const fallowPlan = data?.total_fallow_planned_ha ?? 0;
  const fallowFact = data?.total_fallow_actual_ha ?? 0;
  const totalSown = Math.max(0, (data?.total_actual_ha ?? 0) - fallowFact);
  const totalPlan = Math.max(0, (data?.total_planned_ha ?? 0) - fallowPlan);
  const unsown = Math.max(0, totalPlan - totalSown);
  const pctSown = totalPlan > 0 ? Math.round((totalSown / totalPlan) * 100) : 0;
  // «Под паром» показываем по плановой площади пара — это решение агронома на
  // сезон, а не результат работ. Если плана пара нет вовсе, показываем факт —
  // но подписываем его иначе, чтобы два разных смысла не жили под одним
  // заголовком (нитпик head-web по #829).
  const fallowHa = fallowPlan > 0 ? fallowPlan : fallowFact;
  const fallowIsFactOnly = fallowPlan <= 0 && fallowFact > 0;
  const unplannedHa = data?.total_unplanned_area_ha ?? 0;
  const unplannedFields = data?.total_unplanned_fields ?? 0;
  // Пашня без единого занятия сезона: не пар (никто не решал её парить) и не
  // «осталось засеять» (плана на неё нет). До AK-696 она была невидима, а
  // из-за bool_or-бага читалась как «под паром пол-холдинга».
  const unoccupiedHa = data?.total_unoccupied_area_ha ?? 0;
  const unoccupiedFields = data?.total_unoccupied_fields ?? 0;

  // Заголовок графика — «По культурам» (UAT 05.08 баг 4): «Структура посевных
  // площадей» стояла и здесь, и на таблице RotationCard — два раза подряд.
  // Таблица остаётся с общим именем, график получает содержательное.
  if (!loading && error && !data) {
    // Данных ещё нет — плашка ВМЕСТО графика; если данные были, плашка
    // ложится ПОВЕРХ (см. основную ветку ниже).
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>По культурам</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorState message={error} onRetry={loadRotation} />
        </CardContent>
      </Card>
    );
  }

  if (!loading && (!data || data.items.length === 0)) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>По культурам</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-12">
            Нет посевов в текущем сезоне
          </div>
        </CardContent>
      </Card>
    );
  }

  if (drillCrop) {
    return (
      <Card className="mt-6">
        <CardHeader className="flex-row items-center justify-between space-y-0 gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDrillCrop(null)}
              className="gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />К культурам
            </Button>
            <CardTitle>{drillCrop}</CardTitle>
            <span className="text-sm text-muted-foreground">
              — по хозяйствам
            </span>
          </div>
          {data?.season_code && (
            <span className="text-sm text-muted-foreground">
              {formatSeasonName(data.season_code, data.season_year)}
            </span>
          )}
        </CardHeader>
        <CardContent>
          {drillError && !drillLoading && (
            // ПОВЕРХ прошлой разбивки, если она есть; ВМЕСТО пустоты — если нет.
            <ErrorState
              variant={drillData ? 'inline' : 'block'}
              className={drillData ? 'mb-3' : undefined}
              message={drillError}
              onRetry={() => drillCrop && loadDrill(drillCrop)}
            />
          )}
          {drillLoading ? (
            <div className="h-[380px] flex items-center justify-center text-muted-foreground">
              Загружаем…
            </div>
          ) : drillChartData.length === 0 ? (
            !drillError && (
              <div className="text-center text-muted-foreground py-12">
                Нет данных по хозяйствам
              </div>
            )
          ) : (
            <div className="h-[380px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={drillChartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="crop" tick={{ fontSize: 11 }} />
                  <YAxis
                    label={{
                      value: 'Площадь, га',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 12 },
                    }}
                    tick={{ fontSize: 11 }}
                  />
                  <ChartTooltip
                    formatter={(v) =>
                      `${Number(v).toLocaleString('ru-RU')} га`
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="sown"
                    stackId="a"
                    name="Засеяно"
                    fill={COLORS.sown}
                  />
                  <Bar
                    dataKey="unsown"
                    stackId="a"
                    name="Осталось засеять"
                    fill={COLORS.unsown}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>По культурам</CardTitle>
          {/* Явная семантика периода (вопрос founder «за какой период?»):
              это ВЕСЬ сезон — все культуры года урожая, включая уже убранные,
              а не текущая (осенняя/весенняя) кампания. */}
          <div className="mt-1 text-xs text-muted-foreground">
            план и факт сева — за сезон целиком (все культуры года урожая), не
            за текущую кампанию
          </div>
        </div>
        {data?.season_code && (
          <span className="text-sm text-muted-foreground">
            {formatSeasonName(data.season_code, data.season_year)}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {error && data && (
          // Refetch упал, старые данные лежат — плашка ПОВЕРХ, график не
          // сбрасываем: человек видит устаревшие цифры и знает, что обновление
          // не удалось, вместо молчаливой подмены.
          <ErrorState
            variant="inline"
            className="mb-3"
            message={error}
            onRetry={loadRotation}
          />
        )}
        <div className="mb-4 flex flex-wrap gap-x-6 gap-y-3">
          <div>
            <div className="text-xs text-muted-foreground">Засеяно</div>
            <div
              className="text-2xl font-semibold"
              style={{ color: COLORS.sown }}
            >
              {Math.round(totalSown).toLocaleString('ru-RU')} га
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {pctSown}% от плана
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Осталось засеять</div>
            <div
              className="text-2xl font-semibold"
              style={{ color: COLORS.unsown }}
            >
              {Math.round(unsown).toLocaleString('ru-RU')} га
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">План сева</div>
            <div className="text-2xl font-semibold">
              {Math.round(totalPlan).toLocaleString('ru-RU')} га
            </div>
          </div>
          {fallowHa > 0 && (
            <div>
              <div className="text-xs text-muted-foreground">Под паром</div>
              <div className="text-2xl font-semibold text-muted-foreground">
                {Math.round(fallowHa).toLocaleString('ru-RU')} га
                <span className="ml-2 text-sm font-normal">
                  {fallowIsFactOnly ? 'по факту, не сеется' : 'не сеется'}
                </span>
              </div>
            </div>
          )}
          {/* Незанятая пашня — честными словами, отдельно от пара: «поле
              никто не занял» и «агроном решил парить» — разные вещи. */}
          {unoccupiedHa > 0 && (
            <div>
              <div className="text-xs text-muted-foreground">Без посева и пара</div>
              <div className="text-2xl font-semibold text-muted-foreground">
                {Math.round(unoccupiedHa).toLocaleString('ru-RU')} га
                <span className="ml-2 text-sm font-normal">
                  {unoccupiedFields.toLocaleString('ru-RU')} полей — занятие не заведено
                </span>
              </div>
            </div>
          )}
          {unplannedHa > 0 && (
            <div>
              <div className="text-xs text-muted-foreground">
                Незапланировано
              </div>
              <div className="text-2xl font-semibold text-muted-foreground">
                {Math.round(unplannedHa).toLocaleString('ru-RU')} га
                <span className="ml-2 text-sm font-normal">
                  {unplannedFields.toLocaleString('ru-RU')} полей
                </span>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="h-[380px] flex items-center justify-center text-muted-foreground">
            Загружаем…
          </div>
        ) : (
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                onClick={(e) => {
                  const payload = (
                    e as unknown as {
                      activePayload?: Array<{ payload?: ChartRow }>;
                    }
                  ).activePayload;
                  const crop = payload?.[0]?.payload?.crop;
                  if (crop) setDrillCrop(crop);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="crop" tick={{ fontSize: 11 }} />
                <YAxis
                  label={{
                    value: 'Площадь, га',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12 },
                  }}
                  tick={{ fontSize: 11 }}
                />
                <ChartTooltip
                  // Earth Tech: subtle primary tint вместо дефолтного серого
                  // recharts-cursor'а (серый читается как «выкл»). AK-298.
                  cursor={{ fill: 'hsl(var(--primary) / 0.12)' }}
                  formatter={(v) =>
                    `${Number(v).toLocaleString('ru-RU')} га`
                  }
                />
                <Legend />
                <Bar
                  dataKey="sown"
                  stackId="a"
                  name="Засеяно"
                  fill={COLORS.sown}
                  cursor="pointer"
                >
                  {chartData.map((_, idx) => (
                    <Cell key={idx} cursor="pointer" />
                  ))}
                </Bar>
                <Bar
                  dataKey="unsown"
                  stackId="a"
                  name="Осталось засеять"
                  fill={COLORS.unsown}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <p className="mt-2 text-xs text-muted-foreground">
          💡 Клик по столбцу — разбивка по хозяйствам
        </p>
      </CardContent>
    </Card>
  );
}

export default CropsBarCard;
